/**
 * Create the store with dynamic reducers
 */
import { getGlobalRouter, GlobalRouter } from '@okapi/router-react';
import { configureStore, EnhancedStore } from '@reduxjs/toolkit';
import { createInjectorsEnhancer, forceReducerReload } from 'redux-injectors';
import createSagaMiddleware from 'redux-saga';
import { RootState } from 'types/root-state';
import type { InjectedReducersType } from 'utils/types/injector-typings';
import { createReducerWithInitialState, Env } from './reducers';
import { rootSaga } from './root-saga';

let store: EnhancedStore;

export type CreateStoreParams = {
	router?: GlobalRouter;
	host?: string;
	url?: string;
	preloadedState?: RootState;
	cookie?: string;
	isBot?: boolean;
	env?: Env;
	userAgent?: string;
	injectedReducers?: InjectedReducersType;
};

export function configureAppStore({
	router = getGlobalRouter(),
	host = window?.location.host,
	preloadedState = {} as RootState,
	cookie = document?.cookie,
	isBot = false,
	env = {
		ORCHESTRATOR_HOST: process?.env.ORCHESTRATOR_HOST || '',
		USE_CREDENTIALS: process?.env.USE_CREDENTIALS === '1' || true,
		API_KEY_EXTERNAL: process?.env.API_KEY_EXTERNAL || '',
		ORCHESTRATOR_EXTERNAL_HOST: process?.env.ORCHESTRATOR_EXTERNAL_HOST || null,
		requestID: '',
	},
	userAgent = window?.navigator.userAgent,
	injectedReducers = {},
}: CreateStoreParams): ReturnType<typeof configureStore> {
	const reduxSagaMonitorOptions = {};
	const sagaMiddleware = createSagaMiddleware(reduxSagaMonitorOptions);
	const { run: runSaga } = sagaMiddleware;

	// Create the store with saga middleware
	const middlewares = [sagaMiddleware /* checkJwtMiddleware */];
	const createReducer = createReducerWithInitialState({
		router,
		host,
		env,
		isBot,
		cookie,
		userAgent,
	});

	const enhancers = [
		createInjectorsEnhancer({
			createReducer,
			runSaga,
		}),
	];

	store = configureStore({
		reducer: createReducer(injectedReducers),
		middleware: getDefaultMiddleware =>
			getDefaultMiddleware({ serializableCheck: false }).concat(...middlewares),
		devTools: process.env.NODE_ENV !== 'production',
		enhancers,
		preloadedState,
	});

	runSaga(rootSaga);

	// Make reducers hot reloadable, see http://mxs.is/googmo
	/* istanbul ignore next */
	if (module.hot) {
		module.hot.accept('./reducers', () => {
			forceReducerReload(store);
		});
	}

	return store;
}

export const getStore = (): typeof store => store;
